'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { createClient } from '@/lib/supabase/client';
import { useChat } from '@/lib/hooks/useChat';

const PM_AI_CACHE_KEY = 'openbooq_pm_ai_summary';

export default function PMSmartPickSummary() {
  const { currentUser } = useAuth();
  const supabase = createClient();

  const [pmAiSummary, setPmAiSummary] = useState<string | null>(null);
  const [pmAiLoading, setPmAiLoading] = useState(false);
  const pmAiFetched = useRef(false);

  const { response: pmAiResponse, isLoading: pmAiChatLoading, sendMessage: sendPmAiMessage } = useChat(
    'GEMINI',
    'gemini/gemini-2.5-flash',
    true
  );

  const firstName = currentUser?.first_name ?? 'Project Manager';

  const generatePmAiSummary = useCallback(async () => {
    if (pmAiFetched.current) return;
    pmAiFetched.current = true;

    try {
      const cached = sessionStorage.getItem(PM_AI_CACHE_KEY);
      if (cached) {
        const { text, ts } = JSON.parse(cached);
        if (Date.now() - ts < 30 * 60 * 1000) {
          setPmAiSummary(text);
          return;
        }
      }
    } catch { /* ignore */ }

    setPmAiLoading(true);

    try {
      // Fetch only data scoped to this PM's jobs
      const [jobsRes, materialsRes, qcRes] = await Promise.all([
        supabase
          .from('jobs')
          .select('id, job_status, progress, crew, address, customer, assigned_pm')
          .limit(40),
        supabase
          .from('materials_alerts')
          .select('status, material_name, supplier_name, delivery_date, job_id, notes')
          .in('status', ['delayed', 'pending'])
          .limit(20),
        supabase
          .from('qc_inspections')
          .select('status, result, inspector_name, created_at, job_id')
          .limit(20),
      ]);

      const allJobs = jobsRes.data ?? [];
      const materialsAlerts = materialsRes.data ?? [];
      const qcInspections = qcRes.data ?? [];

      // Scope jobs to this PM (fall back to all if assigned_pm column not populated)
      const pmJobs = allJobs.filter((j: any) =>
        j.assigned_pm === firstName || j.assigned_pm == null || allJobs.length <= 10
      );

      const pmJobIds = new Set(pmJobs.map((j: any) => j.id));

      const activeJobs = pmJobs.filter((j: any) => j.job_status === 'in-progress');
      const delayedJobs = pmJobs.filter((j: any) => j.job_status === 'delayed');
      const scheduledJobs = pmJobs.filter((j: any) => j.job_status === 'scheduled');
      const completedJobs = pmJobs.filter((j: any) => j.job_status === 'completed');

      const avgProgress =
        activeJobs.length > 0
          ? Math.round(activeJobs.reduce((s: number, j: any) => s + (j.progress || 0), 0) / activeJobs.length)
          : 0;

      // Crew issues: jobs with delayed status or low progress
      const crewIssues = activeJobs.filter((j: any) => (j.progress ?? 0) < 30);
      const crewNames = [...new Set(pmJobs.map((j: any) => j.crew).filter(Boolean))];

      // Material delays scoped to PM's jobs
      const pmMaterialDelays = materialsAlerts.filter(
        (m: any) => !m.job_id || pmJobIds.has(m.job_id)
      );
      const delayedMaterials = pmMaterialDelays.filter((m: any) => m.status === 'delayed');
      const pendingMaterials = pmMaterialDelays.filter((m: any) => m.status === 'pending');

      // QC scoped to PM's jobs
      const pmQC = qcInspections.filter(
        (q: any) => !q.job_id || pmJobIds.has(q.job_id)
      );
      const failedQC = pmQC.filter((q: any) => q.result === 'fail' || q.status === 'failed');
      const passedQC = pmQC.filter((q: any) => q.result === 'pass' || q.status === 'passed');
      const pendingQC = pmQC.filter((q: any) => q.status === 'pending' || q.status === 'scheduled');

      const dataContext = `
PM Daily Briefing (${new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}):
PM Name: ${firstName}
Assigned Jobs: ${pmJobs.length} total — ${activeJobs.length} active (avg ${avgProgress}% complete), ${scheduledJobs.length} scheduled, ${delayedJobs.length} delayed, ${completedJobs.length} completed today.
Crews on active jobs: ${crewNames.length > 0 ? crewNames.slice(0, 4).join(', ') : 'None assigned'}.
Crew performance flags: ${crewIssues.length} active job(s) below 30% progress${delayedJobs.length > 0 ? `, ${delayedJobs.length} job(s) in delayed status` : ''}.
Material delays on PM's jobs: ${delayedMaterials.length} delayed${delayedMaterials.length > 0 ? ' (' + delayedMaterials.slice(0, 2).map((m: any) => m.material_name || m.supplier_name || 'material').join(', ') + ')' : ''}, ${pendingMaterials.length} pending delivery.
QC for PM's installs: ${pmQC.length} inspections — ${passedQC.length} passed, ${failedQC.length} failed, ${pendingQC.length} pending.
      `.trim();

      sendPmAiMessage(
        [
          {
            role: 'system',
            content:
              `You are SmartPick™, an AI assistant for a roofing Project Manager at NextRoof 360. Generate a concise daily briefing (3–4 sentences) focused ONLY on what this PM needs to act on today. Cover: the status of their assigned jobs and any delays, crew performance issues on their jobs, material delivery delays affecting their installs, and QC pass/fail results for their jobs. End with 1–2 specific action items for the PM. Do NOT mention company-wide metrics, other departments, or issues outside the PM's direct scope. Be direct and data-driven. Write as flowing prose, no bullet points or headers.`,
          },
          { role: 'user', content: dataContext },
        ],
        { max_completion_tokens: 300 }
      );
    } catch {
      setPmAiLoading(false);
      pmAiFetched.current = false;
    }
  }, [supabase, firstName, sendPmAiMessage]);

  useEffect(() => {
    generatePmAiSummary();
  }, [generatePmAiSummary]);

  useEffect(() => {
    if (pmAiResponse && !pmAiChatLoading) {
      const safeResponse = typeof pmAiResponse === 'string' ? pmAiResponse : String(pmAiResponse);
      setPmAiSummary(safeResponse);
      setPmAiLoading(false);
      try {
        sessionStorage.setItem(PM_AI_CACHE_KEY, JSON.stringify({ text: safeResponse, ts: Date.now() }));
      } catch { /* ignore */ }
    }
  }, [pmAiResponse, pmAiChatLoading]);

  const isLoading = pmAiLoading || pmAiChatLoading;

  const handleRefresh = () => {
    pmAiFetched.current = false;
    setPmAiSummary(null);
    try { sessionStorage.removeItem(PM_AI_CACHE_KEY); } catch { /* ignore */ }
    generatePmAiSummary();
  };

  return (
    <div
      className="rounded-xl p-6 md:p-8 relative overflow-hidden mb-6"
      style={{
        background: 'linear-gradient(135deg, #1C2E5E 0%, #1C2E5E 55%, #E89A3C 100%)',
        boxShadow: '0 8px 32px rgba(0,1,80,0.25)',
      }}
    >
      {/* Decorative circles */}
      <div
        className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #E89A3C, transparent)', transform: 'translate(30%, -30%)' }}
      />
      <div
        className="absolute bottom-0 left-1/3 w-48 h-48 rounded-full opacity-5 pointer-events-none"
        style={{ background: 'radial-gradient(circle, #FFFFFF, transparent)', transform: 'translateY(40%)' }}
      />

      <div className="relative z-10">
        {/* Header row */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div
              className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
              style={{ backgroundColor: 'rgba(232,154,60,0.2)', border: '1px solid rgba(232,154,60,0.4)', color: '#E89A3C' }}
            >
              <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 animate-pulse" />
              SmartPick™ AI · PM Daily Briefing
            </div>
            <span className="text-white/40 text-xs hidden sm:block">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ background: 'rgba(16,185,129,0.25)', color: '#6EE7B7' }}
            >
              PM Insights
            </span>
            {pmAiSummary && !isLoading && (
              <button
                onClick={handleRefresh}
                className="flex items-center gap-1 text-xs text-white/50 hover:text-white/80 transition-colors"
              >
                <Icon name="ArrowPathIcon" size={12} />
                Refresh
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {isLoading ? (
          <div className="space-y-3 max-w-5xl">
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.15)', width: '90%' }} />
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.12)', width: '76%' }} />
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.10)', width: '83%' }} />
            <div className="h-4 rounded-full animate-pulse" style={{ background: 'rgba(255,255,255,0.08)', width: '62%' }} />
            <div className="flex items-center gap-2 mt-2">
              <div className="w-3 h-3 rounded-full border border-cyan-400/50 border-t-cyan-400 animate-spin" />
              <span className="text-xs text-white/40">Analyzing jobs, escalations, QC &amp; logistics data…</span>
            </div>
          </div>
        ) : pmAiSummary ? (
          <p className="text-white text-sm md:text-base leading-relaxed font-light max-w-5xl">
            <span className="font-semibold text-white">Good morning, {firstName}.</span>{' '}
            {typeof pmAiSummary === 'string' ? pmAiSummary : String(pmAiSummary)}
          </p>
        ) : (
          <p className="text-white/60 text-sm italic">PM briefing unavailable. Click Refresh to retry.</p>
        )}

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 mt-5">
          <div className="flex items-center gap-1.5 text-xs text-white/60">
            <Icon name="ClockIcon" size={13} />
            Updated just now
          </div>
          <div className="flex items-center gap-1.5 text-xs text-white/60">
            <Icon name="CpuChipIcon" size={13} />
            {isLoading ? 'Generating…' : 'AI-powered · Live data'}
          </div>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: '#10B981' }}>
            <Icon name="CheckCircleIcon" size={13} />
            My Jobs · Crew · Materials · QC
          </div>
        </div>
      </div>
    </div>
  );
}
